import React, {useEffect, useState} from 'react';
import {Alert, Button, Card, Col, Container, Form, FormControl, InputGroup, Modal, Row, Spinner} from 'react-bootstrap';
import {
    Calendar,
    Check,
    Clock,
    Copy,
    Facebook,
    Linkedin,
    MessageCircle,
    Music,
    PlusCircle,
    Twitter,
    Zap
} from 'react-feather'; // ✅ For create button icon
import {useTranslation} from 'react-i18next';
import {
    createEvent,
    deleteEvent,
    getEvents,
    getPublicRequests,
    getSongRequests,
    getUserProfile,
    updateEvent,
    getWebUrl
} from '../services/api'; //
import {getCurrentUser} from '../services/auth';
import { useNavigate } from 'react-router-dom';



interface Event {
    id: string;
    name: string;
    description?: string;
    location?: string;
    start_date?: string;
    end_date?: string;
    active: boolean;
    created_at: string;
    requestCount?: number;
}

interface SongRequest {
    id: string;
    event_id: string;
    song_name: string;
    artist_name?: string;
    requester_name: string;
    message?: string;
    created_at: string;
    status: 'pending' | 'approved' | 'rejected';
    donation_amount?: number;
}

const Dashboard: React.FC = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const [stats, setStats] = useState({
        totalEvents: 0,
        activeEvents: 0,
        totalRequests: 0,
        pendingRequests: 0,
    });

    const [user, setUser] = useState<any>(null);
    const [events, setEvents] = useState<Event[]>([]);
    const [requests, setRequests] = useState<SongRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [modalShow, setModalShow] = useState(false);
    const [qrUrl, setQrUrl] = useState('');

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [eventToDelete, setEventToDelete] = useState<string | null>(null);
    const [deleting, setDeleting] = useState(false);

    const [showEditModal, setShowEditModal] = useState(false);
    const [editingEvent, setEditingEvent] = useState<Event | null>(null);
    const [editForm, setEditForm] = useState({
        name: '',
        description: '',
        location: '',
        start_date: '',
        end_date: '',
        active: true
    });

    const [showEventModal, setShowEventModal] = useState(false);
    const [eventForm, setEventForm] = useState({
        name: '',
        description: '',
        location: '',
        start_date: '',
        end_date: '',
        active: true
    });
    const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
    const [submitting, setSubmitting] = useState(false);

    const handleCreateEvent = () => {
        setEventForm({
            name: '',
            description: '',
            location: '',
            start_date: '',
            end_date: '',
            active: true
        });
        setFormErrors({});
        setShowEventModal(true);
    };

    const handleEditClick = (event: Event) => {
        setEditingEvent(event);
        setEditForm({
            name: event.name,
            description: event.description || '',
            location: event.location || '',
            start_date: event.start_date ? new Date(event.start_date).toISOString().slice(0, 16) : '',
            end_date: event.end_date ? new Date(event.end_date).toISOString().slice(0, 16) : '',
            active: event.active
        });
        setFormErrors({});
        setShowEditModal(true);
    };
    const handleEditFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const {name, value, type} = e.target;
        const val = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
        setEditForm(prev => ({...prev, [name]: val}));
        if (formErrors[name]) {
            const updatedErrors = {...formErrors};
            delete updatedErrors[name];
            setFormErrors(updatedErrors);
        }
    };
    const handleEditSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm() || !editingEvent) return;
        try {
            setSubmitting(true);
            const response = await updateEvent(editingEvent.id, editForm);
            const updatedEvent = response.data;
            setEvents(events.map(ev => ev.id === updatedEvent.id ? updatedEvent : ev));
            setShowEditModal(false);
        } catch (error: any) {
            setError(error.response?.data?.error || 'Failed to update event');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteClick = (eventId: string) => {
        setEventToDelete(eventId);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (!eventToDelete) return;
        try {
            setDeleting(true);
            await deleteEvent(eventToDelete);
            setEvents(events.filter(event => event.id !== eventToDelete));
            setShowDeleteModal(false);
        } catch (error: any) {
            setError(error.response?.data?.error || 'Failed to delete event');
        } finally {
            setDeleting(false);
            setEventToDelete(null);
        }
    };

    const handleViewRequests = (eventId: string) => {
        window.open(`/events/${eventId}/requests`, '_blank');
    };

    const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const {name, value, type} = e.target;
        const val = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
        setEventForm(prev => ({...prev, [name]: val}));

        if (formErrors[name]) {
            const updatedErrors = {...formErrors};
            delete updatedErrors[name];
            setFormErrors(updatedErrors);
        }
    };
    const validateForm = (): boolean => {
        const errors: { [key: string]: string } = {};
        if (!eventForm.name.trim()) errors.name = 'Name is required';
        if (eventForm.start_date && eventForm.end_date) {
            const start = new Date(eventForm.start_date);
            const end = new Date(eventForm.end_date);
            if (end < start) errors.end_date = 'End date cannot be before start date';
        }
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            setSubmitting(true);
            const response = await createEvent(eventForm);
            const newEvent = response?.data?.event;
            setEvents(prev => [newEvent, ...prev.slice(0, 2)]);
            setStats(prev => ({
                ...prev,
                totalEvents: prev.totalEvents + 1,
                activeEvents: prev.activeEvents + (newEvent.active ? 1 : 0)
            }));
            setShowEventModal(false);
        } catch (error: any) {
            setError(error.response?.data?.error || 'Failed to create event');
        } finally {
            setSubmitting(false);
        }
    };

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            const userProfile = getCurrentUser() || (await getUserProfile()).data;
            setUser(userProfile);

            const eventsRes = await getEvents();
            const eventsData = eventsRes.data;

            const allRequests: SongRequest[] = [];
            let totalRequests = 0;
            let pendingRequests = 0;

            for (const event of eventsData) {
                try {
                    const res = await getPublicRequests(event.id);
                    const reqs: SongRequest[] = res.data;
                    allRequests.push(...reqs);
                    totalRequests += reqs.length;
                    pendingRequests += reqs.filter(r => r.status === 'pending').length;
                    event.requestCount = reqs.length;
                } catch {
                    event.requestCount = 0;
                }
            }

            const sortedEvents = eventsData.sort(
                (a: Event, b: Event) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
            );
            setEvents(sortedEvents);

            const sortedRequests = allRequests.sort(
                (a: SongRequest, b: SongRequest) =>
                    new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
            );
            setRequests(sortedRequests.slice(0, 5));

            const activeEvents = eventsData.filter((e: Event) => e.active).length;
            setStats({totalEvents: eventsData.length, activeEvents, totalRequests, pendingRequests});

        } catch (err: any) {
            setError(err.response?.data?.error || 'Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
    };

    const shareOnLinkedIn = (url: string) => {
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
    };

    const shareOnTwitter = (name: string, url: string) => {
        const text = `Check out this event: ${name}`;
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
    };

    const shareOnFacebook = (url: string) => {
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
    };

    const shareOnWhatsApp = (name: string, url: string) => {
        const text = `🎵 ${name} - check this out! ${url}`;
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    };

    const showQRCode = (url: string) => {
        setQrUrl(url);
        setModalShow(true);
    };

    if (loading) {
        return (
            <Container className="py-5 text-center">
                <Spinner animation="border" variant="primary"/>
                <p>Loading...</p>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="py-5">
                <Alert variant="danger">
                    <Alert.Heading>Error</Alert.Heading>
                    <p>{error}</p>
                    <Button onClick={fetchDashboardData} variant="outline-danger">Retry</Button>
                </Alert>
            </Container>
        );
    }

    return (
        <Container className="py-4">
            <Row className="mb-4">
                <Col>
                    <h1 className="mb-1">{t('dashboard.title')}</h1>
                    {user && (
                        <>
                            <p className="text-muted mb-1">
                                {t('dashboard.welcome')}, {user.name}
                            </p>

                            {user.events_remaining_this_month !== null && (
                                <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center gap-2 gap-sm-3 mt-2 w-100">
                                    <Button
                                        variant="outline-dark"
                                        size="sm"
                                        className="d-flex align-items-center gap-2 shadow-sm w-100 w-sm-auto"
                                        style={{ cursor: 'default' }}
                                        title="This counter resets monthly. You can create more events next month."
                                    >
                                        <Calendar size={16} />
                                        <span
                                            style={{
                                                fontWeight: 500,
                                                fontSize: '0.85rem',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                            className="text-truncate"
                                        >
      {user.events_remaining_this_month} event
                                            {user.events_remaining_this_month !== 1 ? 's' : ''} left this month
    </span>
                                    </Button>

                                    <Button
                                        size="sm"
                                        href="http://localhost:3000/subscription"
                                        className="d-flex align-items-center gap-2 shadow-sm w-100 w-sm-auto"
                                        style={{
                                            backgroundColor: '#795EFF',
                                            border: 'none',
                                            color: 'white',
                                        }}
                                        title="Upgrade to create more events per month"
                                    >
                                        <Zap size={16} />
                                        Upgrade Plan
                                    </Button>
                                </div>

                            )}
                        </>
                    )}
                </Col>

                {/*<Col className="text-end">*/}
                {/*    /!* ✅ FIX: Use react-router Link *!/*/}
                {/*    <Button variant="primary" onClick={handleCreateEvent}>*/}
                {/*        <PlusCircle size={16} className="me-2"/>*/}
                {/*        Create New Event*/}
                {/*    </Button> </Col>*/}

                <Col className="text-end">
                    <Button
                        onClick={handleCreateEvent}
                        className="align-items-center shadow-sm"
                        style={{
                            backgroundColor: '#795EFF', // --primary-color
                            border: 'none',
                            color: 'white'
                        }}
                    >
                        <PlusCircle size={16} />
                        &nbsp; Create New Event
                    </Button>
                </Col>

            </Row>

            <Row className="mb-4">
                <Col lg={3} md={6}>
                    <Card className="h-100 shadow-sm">
                        <Card.Body className="d-flex align-items-center">
                            <div
                                className="icon-circle me-3"
                                style={{
                                    backgroundColor: '#795EFF20', // 12.5% opacity
                                    color: '#795EFF'
                                }}
                            >
                                <Calendar size={24} />
                            </div>
                            <div>
                                <h6 className="mb-0 text-muted">{t('dashboard.stats.totalEvents')}</h6>
                                <h3 className="mb-0">{stats.totalEvents}</h3>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col lg={3} md={6}>
                    <Card className="h-100 shadow-sm">
                        <Card.Body className="d-flex align-items-center">
                            <div
                                className="icon-circle me-3"
                                style={{
                                    backgroundColor: '#4F46E520',
                                    color: '#4F46E5'
                                }}
                            >
                                <Check size={24} />
                            </div>
                            <div>
                                <h6 className="mb-0 text-muted">{t('dashboard.stats.activeEvents')}</h6>
                                <h3 className="mb-0">{stats.activeEvents}</h3>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col lg={3} md={6}>
                    <Card className="h-100 shadow-sm">
                        <Card.Body className="d-flex align-items-center">
                            <div
                                className="icon-circle me-3"
                                style={{
                                    backgroundColor: '#1DA1F220',
                                    color: '#1DA1F2'
                                }}
                            >
                                <Music size={24} />
                            </div>
                            <div>
                                <h6 className="mb-0 text-muted">{t('dashboard.stats.totalRequests')}</h6>
                                <h3 className="mb-0">{stats.totalRequests}</h3>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col lg={3} md={6}>
                    <Card className="h-100 shadow-sm">
                        <Card.Body className="d-flex align-items-center">
                            <div
                                className="icon-circle me-3"
                                style={{
                                    backgroundColor: '#FBBF2420',
                                    color: '#FBBF24'
                                }}
                            >
                                <Clock size={24} />
                            </div>
                            <div>
                                <h6 className="mb-0 text-muted">{t('dashboard.stats.pendingRequests')}</h6>
                                <h3 className="mb-0">{stats.pendingRequests}</h3>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>


            <Row>
                {events.map(event => {
                    const url = `${getWebUrl()}/request/${event.id}`;
                    // const url = `${window.location.protocol}//${window.location.host}/request/${event.id}`;

                    return (
                        <Col md={4} className="mb-4" key={event.id}>
                            <Card className="shadow-sm h-100">
                                <Card.Body className="d-flex flex-column">
                                    <h5 className="card-title">{event.name}</h5>
                                    <p className="text-muted mb-3">Created: {formatDate(event.created_at)}</p>

                                    <InputGroup className="mb-3">
                                        <FormControl readOnly value={url}/>
                                        <Button variant="outline-secondary" onClick={() => copyToClipboard(url)}>
                                            <Copy size={16}/>
                                        </Button>
                                    </InputGroup>

                                    <div className="d-flex gap-2 justify-content-between mb-3">
                                        <Button style={{backgroundColor: '#1DA1F2', border: 'none'}} size="sm"
                                                onClick={() => shareOnTwitter(event.name, url)}>
                                            <Twitter size={16} color="white"/>
                                        </Button>
                                        <Button style={{backgroundColor: '#3b5998', border: 'none'}} size="sm"
                                                onClick={() => shareOnFacebook(url)}>
                                            <Facebook size={16} color="white"/>
                                        </Button>
                                        <Button style={{backgroundColor: '#25D366', border: 'none'}} size="sm"
                                                onClick={() => shareOnWhatsApp(event.name, url)}>
                                            <MessageCircle size={16} color="white"/>
                                        </Button>
                                        <Button style={{backgroundColor: '#0077B5', border: 'none'}} size="sm"
                                                onClick={() => shareOnLinkedIn(url)}>
                                            <Linkedin size={16} color="white"/>
                                        </Button>
                                    </div>

                                    <div className="mb-3">
                                        <Button
                                            className="w-100"
                                            style={{ backgroundColor: '#795EFF', border: 'none', color: 'white' }}
                                            onClick={() => showQRCode(url)}
                                        >
                                            Show QR Code
                                        </Button>
                                    </div>

                                    <div className="d-flex gap-2 justify-content-between mt-auto pt-2 border-top">
                                        <Button variant="outline-warning" size="sm"
                                                onClick={() => handleEditClick(event)}>Edit</Button>
                                        <Button variant="outline-danger" size="sm"
                                                onClick={() => handleDeleteClick(event.id)}>Delete</Button>
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={() => navigate(`/dashboard/event/details/${event.id}`)}
                                        >
                                            View
                                        </Button>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    );
                })}
            </Row>


            <Modal show={modalShow} onHide={() => setModalShow(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Share Event</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center">
                    <img
                        src={`https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(qrUrl)}&size=200x200`}
                        alt="QR Code"
                        className="img-fluid mb-3"
                    />
                    <p className="text-muted small mb-3">Scan this QR code to access the event</p>
                    <Button
                        className="w-100"
                        style={{
                            backgroundColor: '#795EFF',
                            border: 'none',
                            color: 'white'
                        }}
                        onClick={async () => {
                            const imageUrl = `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(qrUrl)}&size=500x500`;
                            const response = await fetch(imageUrl);
                            const blob = await response.blob();
                            const downloadUrl = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = downloadUrl;
                            a.download = 'event-qr.png';
                            document.body.appendChild(a);
                            a.click();
                            a.remove();
                            URL.revokeObjectURL(downloadUrl);
                        }}
                    >
                        Download QR Code
                    </Button>


                </Modal.Body>
            </Modal>
            <Modal show={showEventModal} onHide={() => setShowEventModal(false)} size="lg" backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>{t('event.createEvent.title')}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>{t('event.createEvent.eventName')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={eventForm.name}
                                onChange={handleFormChange}
                                isInvalid={!!formErrors.name}
                            />
                            <Form.Control.Feedback type="invalid">{formErrors.name}</Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>{t('event.createEvent.description')}</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                name="description"
                                value={eventForm.description}
                                onChange={handleFormChange}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowEventModal(false)} disabled={submitting}>
                            {t('event.createEvent.cancelButton')}
                        </Button>
                        <Button type="submit" variant="primary" disabled={submitting}>
                            {submitting ? (
                                <>
                                    <Spinner as="span" animation="border" size="sm" className="me-2"/>
                                    {t('loading')}
                                </>
                            ) : (
                                t('event.createEvent.createButton')
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} backdrop="static" centered>
                <Modal.Header closeButton>
                    <Modal.Title>{t('event.deleteConfirm.title')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{t('event.management.deleteConfirm')}</p>
                    <p className="text-danger fw-bold">{t('event.deleteConfirm.warning')}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
                        {t('event.management.deleteCancel')}
                    </Button>
                    <Button variant="danger" onClick={handleDeleteConfirm} disabled={deleting}>
                        {deleting ? (<><Spinner as="span" animation="border" size="sm"
                                                className="me-2"/>{t('loading')}</>) : t('event.management.deleteConfirmButton')}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>{t('event.createEvent.editTitle')}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleEditSubmit}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>{t('event.createEvent.eventName')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={editForm.name}
                                onChange={handleEditFormChange}
                                isInvalid={!!formErrors.name}
                            />
                            <Form.Control.Feedback type="invalid">{formErrors.name}</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>{t('event.createEvent.description')}</Form.Label>
                            <Form.Control as="textarea" rows={3} name="description" value={editForm.description}
                                          onChange={handleEditFormChange}/>
                        </Form.Group>

                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowEditModal(false)} disabled={submitting}>
                            {t('event.createEvent.cancelButton')}
                        </Button>
                        <Button type="submit" variant="primary" disabled={submitting}>
                            {submitting ? (<><Spinner as="span" animation="border" size="sm"
                                                      className="me-2"/>{t('loading')}</>) : t('event.createEvent.saveButton')}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </Container>
    );
};

export default Dashboard;
